import { useUserStore, useAppStore, useSessionStore, useNotificationStore, useTransformStore } from '@/stores'
import { useAppMachine } from '@/state'
import { useBroadcast, useRepository } from '@/composables'
import { recording, transform } from '@/messages'
import { useTranslation } from 'i18next-vue'
import {jsonRpc, mediator, messenger} from '@communication'
import {StrategyType} from '@communication/StrategyType'


export default class RecordingProcedure {
  constructor() {
    const { t } = useTranslation()
    this.name = 'recordingProcedure'
    this.user = useUserStore()
    this.app = useAppStore()
    this.session = useSessionStore()
    this.notification = useNotificationStore()
    this.transform = useTransformStore()
    this.isRecordingAllowed = false
    this.t = t
  }

  install = () => {
    messenger.subscribe(this.name, 'strategy:data', this.handleData)
    jsonRpc.on('con:ready', this.allowRecording)
    messenger.subscribe(this.name, 'session:participant:disjoined', this.denyRecording)
    messenger.subscribe(this.name, 'strategy:recording:started', this.handleRecordingStarted)
    messenger.subscribe(this.name, 'strategy:recording:stopped', this.handleRecordingStopped)
  }

  uninstall = () => {
    jsonRpc.off('con:ready', this.allowRecording)
    messenger.unsubscribe(this.name)
  }

  allowRecording = (event) => {
    if (event.detail.party === 'client' || this.user.party === 'client') {
      const { send } = useAppMachine()
      this.isRecordingAllowed = true
      send('ALLOW_RECORDING')
    }
  }

  denyRecording = (event) => {
    if (event.party === 'client' && this.user.party === 'assistant') {
      this.isRecordingAllowed = false
      const { send } = useAppMachine()
      if (this.app.state.matches('app.recording.on')) {
        this.stop()
      } else {
        send('DENY_RECORDING')
      }
    }
  }

  start = async () => {
    const currentStrategy = mediator.strategy
    if (currentStrategy !== StrategyType.SFU) {
      messenger.emit('strategy:changing')
    }
    mediator.call(StrategyType.SFU)
    this.notification.showMessage({
      text: this.t('notification.startingRecording'),
      icon: 'record',
    })
    messenger.emit('recording:start', { conference: this.session.conference })
  }

  handleRecordingStarted = () => {
    if (this.user.isAssistant) {
      const { sendWsEvent } = useRepository()
      const { zoom, x, y } = this.transform
      const { width, height } = this.session.transform.client
      sendWsEvent(transform.setMessage({ zoom, x, y }))
      sendWsEvent(transform.viewportMessage({ width, height }))
    }
    this.notification.showMessage({
      text: this.t('notification.recordingStarted'),
      icon: 'record',
      auto: true
    })
    this.app.transition('START_RECORDING')
  }

  stop = async () => {
    this.notification.showMessage({
      text: this.t('notification.stoppingRecording'),
      icon: 'record',
    })
    messenger.emit('recording:stop')
  }

  handleRecordingStopped = () => {
    this.notification.showMessage({
      text: this.t('notification.recordingStopped'),
      icon: 'record',
      auto: true
    })
    this.app.transition('STOP_RECORDING')
    if (!this.isRecordingAllowed) {
      const { send } = useAppMachine()
      send('DENY_RECORDING')
    }
  }

  handleData = (event, channel) => {
    if (channel === 'strategy:data') {
      const { type, data } = event
      switch (type) {
        case 'profile:create': {
          if (this.user.party === 'assistant') {
            if (this.app.state.matches('app.recording.on')) {
              const broadcast = useBroadcast()
              broadcast(recording.syncMessage(true))
            }
          }
          break
        }
        case 'recording:sync': {
          if (!this.app.state.matches('app.recording.on')) {
            this.handleRecordingStarted()
          }
        }
      }
    }
  }
}
